import { sparkRegisteredTotal } from "@greenflagdate/shared";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { UpcomingEligibleWidget } from "../../../../components/UpcomingEligibleWidget/UpcomingEligibleWidget";
import { isSparkFullFor } from "../../../../lib/sparkHelpers";
import { inviterCountBySpark } from "../../../../lib/inviterCounts";
import { useStoreData } from "../../../../lib/store/store";
import type { EventsWidgetProvider } from "./types";

export const upcomingEligibleProvider: EventsWidgetProvider = {
  id: "upcoming-eligible",

  match({ store, sparks, now }) {
    const user = store.user;
    const age = user?.birth_date
      ? dayjs().diff(user.birth_date, "years")
      : null;

    const eligible = sparks
      .filter((spark) => {
        if (spark.registered || spark.waitlist) return false;
        if (new Date(spark.ends_at).getTime() < now) return false;
        if (age !== null) {
          if (age < spark.min_age) return false;
          if (spark.max_age && age > spark.max_age) return false;
        }
        if (user) {
          if (isSparkFullFor(user.gender, spark)) return false;
        } else if (
          sparkRegisteredTotal(spark.registrationSummary) >= spark.capacity
        ) {
          return false;
        }
        return true;
      })
      .sort(
        (a, b) =>
          new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime()
      );

    const spark = eligible[0];
    if (!spark) return null;

    return {
      key: `upcoming-eligible-${spark.id}`,
      subheading: "An event you might like",
      featuredSparkId: spark.id,
      render: () => <Renderer spark={spark} />,
    };
  },
};

function Renderer({
  spark,
}: {
  spark: import("@greenflagdate/shared").UpcomingSparkForUser;
}) {
  const navigate = useNavigate();
  const storeData = useStoreData();
  const goToDetails = () =>
    navigate(`/event/atlanta/${spark.id}`, { state: { animate: true } });
  // Add the viewer's inviter(s) back into the count — this widget doesn't
  // render the inviter separately, so without it an invited user's chip
  // undercounts or disappears. See inviterCountBySpark.
  const inviterCount =
    inviterCountBySpark(storeData.incomingInvitations).get(spark.id) ?? 0;
  return (
    <UpcomingEligibleWidget
      sparkId={spark.id}
      title={spark.title}
      startsAt={spark.starts_at}
      img={spark.primary_image || spark.hero_image || ""}
      sparks={(spark.attendingLikedCount + inviterCount) || undefined}
      sparkAvatars={spark.attendingLikedAvatars.map((a) => a.url)}
      onSignUp={goToDetails}
    />
  );
}
